/**
 * External Imports
 */
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { format, isYesterday, subDays } from 'date-fns';

/**
 * Internal Imports
 */
import { useParams } from 'react-router-dom';
import RoundHeader from './header';
import RoundFooter from './RoundFooter';
import Survey from './survey';
import CloseBtn from '../shared/buttons/CloseBtn';
import Loading from '../shared/Loading';

import { RootState } from '../../redux/store';
import { clearRoundState, setRoundStarted } from '../../redux/actions';
import {
  useGetMasterTemplatesQuery,
  useGetPublishedTemplatesByLocationAndTypeQuery,
  useLazyGetFullRoundSurveyQuery
} from '../../redux/apis/Templates.api';

import { date } from '../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  Employee,
  FeedbackPost,
  ICondensedMasterTemplate,
  IFacility,
  IFullTemplate,
  Patient,
  RoundAnswer,
  TaskPost
} from '../../config/interfaces';

/**
 * Style Imports
 */
import { FinishRoundTypography, RoundInfoContainer } from './Round.styles';

/**
 * Static File Imports
 */
import finishedRoundIcon from '../../assets/images/complete_round_icon.png';

/**
 * Constant Imports
 */
import supportRoundingTypes from '../../constants/supportRoundingTypes.json';

interface RoundProps {
  currentRound: any;
  facilityId: string;
  facilities: IFacility[];
  fetchingRound: boolean;
  isRoundStarted: boolean;
  isRoundCompleted: boolean;
  pendingFeedback: FeedbackPost[];
  pendingIssues: TaskPost[];
  prevTemplate: string;
  selectedEmployee?: Employee | null;
  selectedPatient?: Patient | null;
  templateById: IFullTemplate;
  type: string;
  userId: string;
}

const supportMasterTemplateConfigIds = {
  csc: 'cd9d74f0-a9f7-11f0-87a6-33da270f5965',
  cned: 'c2a6d060-48fe-11ef-aeff-afe93ea5e8fd'
} as Record<any, string>;

const CompleteRoundView = () => (
  <div className="d-flex align-self-center align-items-center mt-6">
    <img src={finishedRoundIcon} alt="checkmark in circle with ribbon edge" />
    <FinishRoundTypography>You just completed a round!</FinishRoundTypography>
  </div>
);

const NoTemplateView = () => (
  <div className="d-flex align-self-center align-items-center mt-6">
    <FinishRoundTypography>No template found!</FinishRoundTypography>
  </div>
);

const Round: FC<RoundProps> = ({
  currentRound,
  facilities,
  facilityId,
  fetchingRound,
  isRoundStarted,
  isRoundCompleted,
  pendingFeedback,
  pendingIssues,
  prevTemplate,
  selectedEmployee,
  selectedPatient,
  templateById,
  type,
  userId
}) => {
  const dispatch = useDispatch();

  const [selectedTemplate, setSelectedTemplate] =
    useState<ICondensedMasterTemplate>();
  const [day, setDay] = useState<string>('today');
  const [selectedTime, setSelectedTime] = useState<string>(
    format(new Date(), 'HH:mm')
  );
  const [selectedDateTime, setSelectedDateTime] = useState<string>(
    date.convertToDateTime(new Date())
  );
  const { validationType = '' } = useParams();
  const userFacility = useMemo(
    () => facilities.filter(facility => facility.facilityId === facilityId)[0],
    [facilities, facilityId]
  );

  const {
    data: masterTemplatesData
    // isError: isGetMasterTemplatesError,
    // isFetching: isGetMasterTemplatesFetching,
    // isLoading: isGetMasterTemplatesLoading
  } = useGetMasterTemplatesQuery(
    { archived: false, condensed: true },
    {
      refetchOnMountOrArgChange: 300,
      skip: type === 'patient'
    }
  );
  /**
   * TODO: The above should really call
   * getActiveTemplates({
   *   archived: false,
   *   condensed: true,
   *   roundingType: 'employee' ||
   *   roundingType: supportType === 'csc' ? 'csrn' : ['employee', 'cned']
   *   roundingType: validationType === 'csc' ? 'csrn' : ['employee', 'cned']
   * })
   */
  const {
    data: publishedTemplatesByLocationAndTypeData
    // isError: isGetPublishedTemplatesByLocationAndTypeError,
    // isFetching: isGetPublishedTemplatesByLocationAndTypeFetching,
    // isLoading: isGetPublishedTemplatesByLocationAndTypeLoading
  } = useGetPublishedTemplatesByLocationAndTypeQuery(
    {
      archived: false,
      condensed: false,
      division: userFacility?.division ?? '',
      facilityId,
      roundingType: 'patient',
      unitId: selectedPatient?.unitId ?? ''
    },
    {
      refetchOnMountOrArgChange: 15,
      skip:
        type !== 'patient' ||
        !facilityId ||
        !userFacility.division ||
        !selectedPatient?.unitId
    }
  );
  const [
    getFullRoundSurvey,
    {
      data: fullRoundSurveyData,
      // isError: isGetFullRoundSurveyError,
      isFetching: isGetFullRoundSurveyFetching
      // isLoading: isGetFullRoundSurveyLoading
    }
  ] = useLazyGetFullRoundSurveyQuery({});

  const getCombinedAnswers = () => {
    return currentRound.answers?.reduce((acc: string, answer: RoundAnswer) => {
      let answerStr = '';
      if (answer.answer === null || answer.answer.length === 0) {
        return `${acc}#`; // we need a placeholder even if there is no answer
      }
      answer.answer.forEach(element => {
        answerStr += element.value;
      });
      return acc + answerStr;
    }, '');
  };

  const handleCloseClick = useCallback(() => {
    setDay('today');
    setSelectedTime(format(new Date(), 'HH:mm'));
    setSelectedDateTime(date.convertToDateTime(new Date()));
    dispatch(clearRoundState());
  }, [type]);

  const handleDateTimeChange = useCallback(
    (e: any) =>
      type === 'patient'
        ? setSelectedTime(e.detail.value)
        : setSelectedDateTime(e),
    [type]
  );

  const handleTemplateSelect = useCallback(
    (template: ICondensedMasterTemplate) => {
      setSelectedTemplate(template);
    },
    []
  );

  const createdByCurrentUser = useMemo(
    () =>
      currentRound &&
      Object.keys(currentRound).length > 0 &&
      currentRound.userId === userId,
    [currentRound, userId]
  );

  const isPast24Hrs = useMemo(
    () =>
      currentRound &&
      Object.keys(currentRound).length > 0 &&
      date.isOverOneDayOld(currentRound.origRoundDate),
    [currentRound]
  );

  const isNew = useMemo(() => !currentRound.lastEditDate, [currentRound]);
  const isEdit = useMemo(
    () => currentRound?.lastEditDate && !isPast24Hrs && createdByCurrentUser,
    [currentRound, createdByCurrentUser, isPast24Hrs]
  );

  const isViewOnly = useMemo(
    () => currentRound?.lastEditDate && (isPast24Hrs || !createdByCurrentUser),
    [currentRound, createdByCurrentUser, isPast24Hrs]
  );

  const isPastTemplateNotFound = useMemo(
    () => !isNew && !selectedTemplate,
    [isNew, selectedTemplate]
  );

  const adjustTime = useMemo(() => {
    const adjTime = new Date();
    // Patient round timestamp
    if (type === 'patient') {
      if (selectedTime) {
        adjTime.setHours(
          Number(selectedTime.split(':')[0]),
          Number(selectedTime.split(':')[1])
        );
      }
      if (day === 'yesterday')
        return date.convertDateToUTCTimestamp(subDays(adjTime, 1));
      return date.convertDateToUTCTimestamp(adjTime);
    }
    // Emp round timestamp
    const [year, month, rest] = selectedDateTime.split('-');
    if (month) {
      const [dayOfMonth, time] = rest.split('T');
      adjTime.setFullYear(Number(year), Number(month) - 1, Number(dayOfMonth));
      adjTime.setHours(Number(time.split(':')[0]), Number(time.split(':')[1]));
    }
    return date.convertDateToUTCTimestamp(adjTime);
  }, [selectedTime, selectedDateTime, day]);

  const masterTemplateOptions = useMemo(() => {
    if (type === 'patient') {
      return (
        publishedTemplatesByLocationAndTypeData?.filter(
          template => template.roundingType === type
        ) || []
      );
    }
    return (
      masterTemplatesData?.filter(template =>
        isNew
          ? template.roundingType === (type === 'csc' ? 'csrn' : type)
          : currentRound.roundingType &&
            template.roundingType === currentRound.roundingType
      ) || []
    );
  }, [
    currentRound,
    isNew,
    masterTemplatesData,
    publishedTemplatesByLocationAndTypeData,
    type
  ]);
  const defaultTemplate = useMemo(() => {
    if (type === 'patient') {
      return prevTemplate
        ? masterTemplateOptions.find(
            temp => temp.templateConfigId === prevTemplate
          )
        : masterTemplateOptions[0];
    }
    if (type === 'employee') {
      // Employee rounds are defaulted based on direct report templates 30, 60, 90 or standard
      const today: any = new Date();

      // Check that effectiveStartDate exists and is not string null for vendors
      let selEmpStartDate;

      if (
        selectedEmployee?.effectiveStartDate &&
        selectedEmployee?.effectiveStartDate.includes('/')
      ) {
        const dateSplit = selectedEmployee?.effectiveStartDate.split('/');
        const formatToOrbitDate = dateSplit[2] + dateSplit[0] + dateSplit[1];
        selEmpStartDate = formatToOrbitDate;
      } else if (
        selectedEmployee?.effectiveStartDate &&
        selectedEmployee?.effectiveStartDate !== 'NULL'
      ) {
        selEmpStartDate = selectedEmployee?.effectiveStartDate;
      } else {
        selEmpStartDate = date.beginningOfYear();
      }

      const formattedStDt: any = selEmpStartDate?.slice(0, 9);
      const prettyStDt: any = date.datePrettier(formattedStDt);

      const date2: any = new Date(prettyStDt); // Employee Start Date effectiveStartDate prop

      const empDaysSinceHire = Math.abs(today - date2); // subtract start day from today
      const daysSinceHired = Math.ceil(
        empDaysSinceHire / (1000 * 60 * 60 * 24)
      ); // calc days

      let template: ICondensedMasterTemplate[];
      if (daysSinceHired <= 31) {
        template = masterTemplateOptions.filter(temp =>
          temp.name.includes('Day 30')
        );
        return template[0];
      }
      if (daysSinceHired >= 32 && daysSinceHired <= 61) {
        template = masterTemplateOptions.filter(
          temp => temp.name === 'Day 60 Employee Rounding'
        );
        return template[0];
      }
      if (daysSinceHired >= 61 && daysSinceHired <= 91) {
        template = masterTemplateOptions.filter(
          temp => temp.name === 'Day 90 Employee Rounding'
        );
        return template[0];
      }
      if (daysSinceHired > 91 || !daysSinceHired) {
        template = masterTemplateOptions.filter(
          temp => temp.name === 'Standard Employee Rounding'
        );
        return template[0];
      }
      return undefined;
    }
    if (type === 'validation') {
      return masterTemplateOptions.find(
        temp => temp.name.toLowerCase() === `${validationType} validation`
      );
    }
    if (supportRoundingTypes.validation.includes(type)) {
      return masterTemplateOptions.find(temp => temp.roundingType === type);
    }
    return masterTemplateOptions.find(
      temp => temp.templateConfigId === supportMasterTemplateConfigIds[type]
    );
  }, [masterTemplateOptions, selectedEmployee, type]);

  const roundDate = useMemo(() => {
    return currentRound.roundDate;
  }, [currentRound.origRoundDate]);

  const originalAnswers = useMemo(() => {
    return getCombinedAnswers();
  }, [currentRound.origRoundDate]);

  const hasChanges = useMemo(() => {
    if (pendingFeedback.length > 0 || pendingIssues.length > 0) {
      return true;
    }
    const currentAnswers = getCombinedAnswers();
    if (currentAnswers !== originalAnswers) {
      return true;
    }
    if (!currentRound.roundDate || !roundDate) {
      return false;
    }
    return currentRound.roundDate.slice(0, -2) !== roundDate.slice(0, -2);
  }, [
    currentRound.answers,
    currentRound.roundDate,
    pendingFeedback,
    pendingIssues
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearRoundState());
    };
  }, []);

  useEffect(() => {
    if (isNew) setSelectedTemplate(defaultTemplate as ICondensedMasterTemplate);
  }, [defaultTemplate, isNew]);

  useEffect(() => {
    if (!isNew)
      setSelectedTemplate(
        masterTemplateOptions.find(
          temp => temp.templateConfigId === currentRound.templateId
        )
      );
  }, [currentRound, isNew]);

  useEffect(() => {
    if (!isNew && Object.keys(templateById).length > 0)
      setSelectedTemplate(templateById);
  }, [isNew, masterTemplateOptions, templateById]);

  useEffect(() => {
    if (!fetchingRound && !isNew && !isRoundStarted && selectedTemplate)
      dispatch(setRoundStarted());
  }, [fetchingRound, isNew, isRoundStarted, selectedTemplate]);

  useEffect(() => {
    if (
      selectedTemplate &&
      Object.keys(selectedTemplate).length > 0 &&
      isRoundStarted
    ) {
      // TODO: Not loving the use of unlisted here for emp surveys as well
      if (userId && selectedPatient?.unitId) {
        getFullRoundSurvey(
          {
            templateConfigId: selectedTemplate.templateConfigId,
            includeArchived: isViewOnly ?? false,
            division: (userFacility?.division ?? '').replace(/%20/g, '+'),
            facilityId,
            unitId: selectedPatient.unitId
          },
          false
        );
      } else if (userId && selectedEmployee) {
        getFullRoundSurvey(
          {
            templateConfigId: selectedTemplate.templateConfigId,
            includeArchived: isViewOnly ?? false
          },
          false
        );
      }
    }
  }, [
    facilityId,
    isRoundStarted,
    selectedPatient?.unitId,
    selectedEmployee,
    isViewOnly,
    selectedTemplate,
    userFacility?.division
  ]);

  useEffect(() => {
    if (
      currentRound &&
      currentRound.roundDate &&
      date.isOrbitTimestamp(currentRound.roundDate) &&
      type === 'patient'
    ) {
      setDay(
        isYesterday(date.parseDate(currentRound.roundDate))
          ? 'yesterday'
          : 'today'
      );
      setSelectedTime(format(date.parseDate(currentRound.roundDate), 'HH:mm'));
    } else if (
      currentRound &&
      currentRound.roundDate &&
      date.isOrbitTimestamp(currentRound.roundDate)
    ) {
      setSelectedDateTime(
        date.convertToDateTime(date.parseDate(currentRound.roundDate))
      );
    }
  }, [currentRound.roundDate]);

  return (
    <div
      data-testid="round-component"
      className="d-flex flex-column w-100 overflow-auto neu-bg-gray-0"
    >
      {isRoundCompleted ? (
        <>
          <RoundInfoContainer>
            <div className="ps-5">
              <CloseBtn text="Round" handleCloseClick={handleCloseClick} />
            </div>
          </RoundInfoContainer>
          <CompleteRoundView />
        </>
      ) : isPastTemplateNotFound ? (
        <>
          <RoundInfoContainer>
            <div className="ps-5">
              <CloseBtn text="Round" handleCloseClick={handleCloseClick} />
            </div>
          </RoundInfoContainer>
          <NoTemplateView />
        </>
      ) : !fetchingRound ? (
        <>
          <RoundHeader
            day={day}
            hideCloseButton={isNew || !currentRound.id}
            isNew={isNew}
            isRoundStarted={isRoundStarted}
            isViewOnly={isViewOnly}
            masterTemplateOptions={masterTemplateOptions}
            roundBtnEnabled={Boolean(selectedTemplate)}
            selectedTemplate={selectedTemplate}
            selectedDateTime={selectedDateTime}
            selectedTime={selectedTime}
            type={type}
            handleCloseClick={handleCloseClick}
            handleDateTimeChange={handleDateTimeChange}
            handleTemplateSelect={handleTemplateSelect}
            setDay={setDay}
          />
          {isRoundStarted && (
            <>
              <Survey
                chosenSurvey={fullRoundSurveyData}
                isNew={isNew}
                isViewOnly={isViewOnly}
                loading={isGetFullRoundSurveyFetching}
                selectedEmployee={selectedEmployee}
                selectedPatient={selectedPatient}
                selectedTemplate={selectedTemplate}
                selectedTimestamp={adjustTime}
                type={type}
              />
              {!isViewOnly &&
                (selectedPatient || selectedEmployee) &&
                !isGetFullRoundSurveyFetching &&
                fullRoundSurveyData &&
                Object.keys(currentRound).length > 0 && (
                  <RoundFooter
                    chosenSurvey={fullRoundSurveyData}
                    isEdit={isEdit}
                    hasChanges={hasChanges}
                    selectedEmployee={selectedEmployee}
                    selectedPatient={selectedPatient}
                    type={type}
                  />
                )}
            </>
          )}
        </>
      ) : (
        <Loading label="round" />
      )}
    </div>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  currentRound: state.RoundReducer.currentRound,
  fetchingRound: state.RoundReducer.fetchingRound,
  facilities: state.ConfigReducer.userAccessibleFacilities,
  facilityId: state.AuthorizedUser.authorizedUser.facilityId,
  isRoundStarted: state.RoundReducer.isRoundStarted,
  isRoundCompleted: state.RoundReducer.isRoundCompleted,
  pendingFeedback: state.RoundReducer.pendingFeedbacks,
  pendingIssues: state.RoundReducer.pendingIssues,
  prevTemplate: state.TemplateReducer.prevTemplate,
  templateById: state.TemplateReducer.templateById,
  userId: state.AuthorizedUser.authorizedUser.hcaid
});

export default connect(mapReduxStateToProps)(Round);
